import { useEffect, useMemo } from "react";

import type { DramaSeries } from "~/api/drama";

export interface SeoHead {
  title: string;
  description: string;
  imageUrl: string | undefined;
  url: string;
  jsonLd: Record<string, unknown>;
}

const DESCRIPTION_LENGTH = 160;

// One fixed host, so that a copy of a page on another host names the blidz.com page as canonical
export const SITE_ORIGIN = "https://blidz.com";

/**
 * Cuts the text at the last space before the limit. The limit counts code points, so that the cut
 * never splits an emoji.
 */
export function shortenDescription(text: string, limit = DESCRIPTION_LENGTH): string {
  const chars = Array.from(text);
  if (chars.length <= limit) return text;
  const space = chars.lastIndexOf(" ", limit - 2);
  return `${chars.slice(0, space >= limit / 2 ? space : limit - 1).join("")}…`;
}

/** Joins the text of the elements with a space, so that `<li>a</li><li>b</li>` gives "a b". */
export function htmlToText(html: string): string {
  // DOMParser builds an inert document: its scripts do not run and its images do not load.
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style").forEach(element => element.remove());
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const parts: string[] = [];
  while (walker.nextNode()) parts.push(walker.currentNode.textContent ?? "");
  return parts.join(" ").split(/\s+/).filter(Boolean).join(" ");
}

/**
 * Sets an attribute on the `<tag key="name">` element of the head, or adds that element. The
 * returned function puts back the old value, or removes the added element.
 */
function setHeadElement(tag: string, key: string, name: string, attribute: string, value: string): () => void {
  const existing = document.head.querySelector(`${tag}[${key}="${name}"]`);
  if (existing) {
    const old = existing.getAttribute(attribute);
    existing.setAttribute(attribute, value);
    return () => {
      if (old === null) existing.removeAttribute(attribute);
      else existing.setAttribute(attribute, old);
    };
  }
  const element = document.createElement(tag);
  element.setAttribute(key, name);
  element.setAttribute(attribute, value);
  document.head.appendChild(element);
  return () => element.remove();
}

/**
 * Writes the page's title, description, Open Graph tags, canonical link and JSON-LD into the head.
 * The title and the tags are edited in place rather than rendered as React `<title>` and `<meta>`,
 * because a document with two titles has no defined title. The returned function restores the head
 * as it was.
 */
export function applySeoHead(head: SeoHead): () => void {
  const description = shortenDescription(head.description);
  const oldTitle = document.title;
  document.title = head.title;

  const restores = [
    setHeadElement("meta", "property", "og:title", "content", head.title),
    setHeadElement("meta", "property", "og:description", "content", description),
    setHeadElement("meta", "property", "og:url", "content", head.url),
    setHeadElement("meta", "name", "description", "content", description),
    setHeadElement("link", "rel", "canonical", "href", head.url),
  ];
  if (head.imageUrl) restores.push(setHeadElement("meta", "property", "og:image", "content", head.imageUrl));

  const script = document.createElement("script");
  script.type = "application/ld+json";
  // A serialized DOM would end the script at a "</script>" inside the text; "\u003c" keeps it whole.
  script.textContent = JSON.stringify(head.jsonLd).replace(/</g, "\\u003c");
  document.head.appendChild(script);

  return () => {
    script.remove();
    restores.forEach(restore => restore());
    document.title = oldTitle;
  };
}

/**
 * The head of a series. The canonical link is the series page, also for the watch page of an episode.
 * Null unless `series` is the series of `seriesId`, so that a series left in state from the previous
 * query never names the page.
 */
export function dramaSeriesSeoHead(series: DramaSeries | null, seriesId: string | null): SeoHead | null {
  if (!series || series.id !== seriesId) return null;
  const url = `${SITE_ORIGIN}/cinema/detail?series=${encodeURIComponent(series.id)}`;
  const description = series.description || series.title;
  const jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "TVSeries",
    name: series.title,
    description,
    url,
  };
  // Structured data validators reject an empty list
  if (series.thumbnail) jsonLd.image = [series.thumbnail];
  if (series.tags?.length) jsonLd.genre = series.tags;
  if (series.episodeCount) jsonLd.numberOfEpisodes = series.episodeCount;
  return { title: `${series.title} | Blidz`, description, imageUrl: series.thumbnail, url, jsonLd };
}

/** Applies the head while the page shows `head`; null leaves the head of the root layout. */
export function useSeoHead(head: SeoHead | null): void {
  useEffect(() => (head ? applySeoHead(head) : undefined), [head]);
}

export function useDramaSeriesSeoHead(series: DramaSeries | null, seriesId: string | null): void {
  useSeoHead(useMemo(() => dramaSeriesSeoHead(series, seriesId), [series, seriesId]));
}
